import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Phone, Mail, Car, Search, Users } from "lucide-react";
import { API, auth, fmtDate, handleAuthError } from "./shared";

const fmt = (v) => `${Math.round(v).toLocaleString("fr-FR")} FCFA`;
const REPAIR = { recu: "Reçu", en_cours: "En cours", pret: "Prêt", livre: "Livré" };

const AdminCustomers = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/customers`, auth(token)).then((r) => setItems(r.data)).catch((err) => handleAuthError(err, logout, () => {}));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const needle = q.trim().toLowerCase().replace(/\s/g, "");
  const flat = (s) => (s || "").toLowerCase().replace(/\s/g, "");
  const visible = items.filter((c) => !needle || flat(c.name).includes(needle) || flat(c.phone).includes(needle) || flat(c.vehicles.join(" ")).includes(needle));

  return (
    <div data-testid="admin-customers-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest"><Users className="h-5 w-5 text-gold" /> Historique clients</h2>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-brand" data-testid="customers-count">{items.length} client(s) · regroupés par numéro de téléphone</p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, téléphone ou véhicule" data-testid="customers-search-input" className="w-72 rounded-full border border-brand/20 bg-white py-2.5 pl-11 pr-4 text-sm text-forest outline-none focus:border-gold focus:ring-4 focus:ring-gold/10" />
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {visible.map((c, i) => {
          const isOpen = open === c.phone;
          return (
            <div key={c.phone + i} className="rounded-2xl border border-brand/10 bg-white" data-testid={`customer-${i}`}>
              <button onClick={() => setOpen(isOpen ? null : c.phone)} data-testid={`customer-${i}-toggle`} className="flex w-full flex-wrap items-center justify-between gap-4 px-6 py-4 text-left">
                <div className="flex items-center gap-4">
                  <span className="font-display flex h-11 w-11 items-center justify-center rounded-xl bg-mint text-base font-extrabold text-brand">{c.name.trim().charAt(0).toUpperCase() || "?"}</span>
                  <div>
                    <p className="font-display text-base font-bold text-forest" data-testid={`customer-${i}-name`}>{c.name}</p>
                    <p className="flex flex-wrap items-center gap-3 text-xs text-brand">
                      <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>
                      {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                      {c.vehicles.length > 0 && <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" />{c.vehicles.join(", ")}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <div><p className="font-display text-xl font-extrabold text-gold" data-testid={`customer-${i}-count`}>{c.count}</p><p className="font-mono text-[9px] uppercase tracking-widest text-brand">demande(s)</p></div>
                  {c.total_invoiced > 0 && <div><p className="font-display text-base font-bold text-forest">{fmt(c.total_invoiced)}</p><p className="font-mono text-[9px] uppercase tracking-widest text-brand">facturé</p></div>}
                  <ChevronDown className={`h-5 w-5 text-brand transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <ul className="border-t border-brand/10 px-6 py-4" data-testid={`customer-${i}-history`}>
                  {c.quotes.map((h) => (
                    <li key={h.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-brand/5 py-3 text-sm last:border-0">
                      <div>
                        <p className="font-semibold text-forest">{h.service}{h.vehicle ? ` · ${h.vehicle}` : ""}</p>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-brand">{fmtDate(h.created_at)}{h.date ? ` · RDV ${h.date} ${h.time || ""}` : ""} · {h.tracking_code}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-widest">
                        {h.repair_status && <span className="rounded-full bg-brand px-2.5 py-1 text-white">{REPAIR[h.repair_status]}</span>}
                        {h.estimate_total != null && <span className={`rounded-full px-2.5 py-1 ${h.estimate_decision === "accepted" ? "bg-mint text-brand" : h.estimate_decision === "refused" ? "bg-cream text-gold" : "border border-brand/20 text-brand"}`}>Devis {fmt(h.estimate_total)}{h.estimate_decision === "accepted" ? " ✓" : h.estimate_decision === "refused" ? " ✗" : ""}</span>}
                        {h.invoice_number && <span className="rounded-full bg-gold px-2.5 py-1 text-white">Facture {h.invoice_number}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
        {visible.length === 0 && <p className="rounded-2xl border-2 border-dashed border-brand/20 px-6 py-10 text-sm text-brand">Aucun client trouvé.</p>}
      </div>
    </div>
  );
};

export default AdminCustomers;
